import Vue from 'vue';
import _ from '../../utils';
import ServicelayerUtils from './servicelayerUtils';
import ServiceResponse, { RawServiceResponse } from './responseTypes/serviceResponse';
import ServiceCache from './serviceCache';
import { ApiMethod } from './apiMethod';
import Interceptor from './interceptor';
import RequestConfig, { requestConfigConverter } from './requestConfig';
import { CoreServiceAPI as CoreServiceAPIV1 } from '../../services/v1/index';
import { CoreServiceAPIV2 } from '../../services/v2/index';
import { ExtendedFetchFunctionReturn } from '../fetch/fetch';

export interface ServiceAPI {
  // Declared in App
}

export interface ServiceAPIV2 {
  // Declared in App
}

export enum StatusType {
  Unknown,
  Informational,
  Success,
  Redirection,
  ClientError,
  ServerError,
}

/** Retrieves the response status type based on the [status code](https://developer.mozilla.org/en-US/docs/Web/HTTP/Status) */
export const getStatusType = (status?: string | number | undefined | null): StatusType => {
  const parsedStatus = typeof status === 'string' ? Number.parseInt(status, 10) : status;
  if (parsedStatus) {
    if (parsedStatus >= 100 && parsedStatus < 200) return StatusType.Informational;
    if (parsedStatus >= 200 && parsedStatus < 300) return StatusType.Success;
    if (parsedStatus >= 300 && parsedStatus < 400) return StatusType.Redirection;
    if (parsedStatus >= 400 && parsedStatus < 500) return StatusType.ClientError;
    if (parsedStatus >= 500 && parsedStatus < 600) return StatusType.ServerError;
  }
  return StatusType.Unknown;
}

export default class Servicelayer {
  // Every version needs to be defined explicitly for the typing to work
  get v1() {
    const self = this;
    // For legacy reasons V1 maps to api directly
    return {
      get api() { return self.api; },
      set api(value) { self.api = value; },
    };
  }
  v2 = {} as { api: CoreServiceAPIV2 & ServiceAPIV2 };

  /** @deprecated Use a specific service version ```service.v2.api``` */
  api = {} as CoreServiceAPIV1 & ServiceAPI;
  utils = { ...ServicelayerUtils(this) };
  cache = new ServiceCache<Promise<any>>();
  interceptors: Interceptor[] = [];

  get<T = RawServiceResponse<any>>(url: string, params?: RequestConfig): Promise<T> {
    return this.performRequest<T>('get', url, params);
  }
  put<T = RawServiceResponse<any>>(url: string, params?: RequestConfig): Promise<T> {
    this.clearCache();
    return this.performRequest<T>('put', url, params);
  }
  post<T = RawServiceResponse<any>>(url: string, params?: RequestConfig): Promise<T> {
    this.clearCache();
    return this.performRequest<T>('post', url, params);
  }
  delete<T = RawServiceResponse<any>>(url: string, params?: RequestConfig): Promise<T> {
    this.clearCache();
    return this.performRequest<T>('delete', url, params);
  }
  patch<T = RawServiceResponse<any>>(url: string, params?: RequestConfig): Promise<T> {
    this.clearCache();
    return this.performRequest<T>('patch', url, params);
  }

  clearCache() {
    this.cache.clear();
  }

  async performRequest<T = any>(requestType: ApiMethod, apiEndpoint: string, config?: RequestConfig): Promise<T> {
    if (apiEndpoint.startsWith('/')) {
      apiEndpoint = apiEndpoint.substring(1, apiEndpoint.length);
    }

    if (!config || !config.skipInterceptors) {
      const interceptorPromises = [] as Array<Promise<any>>;
      this.interceptors.forEach((interceptor) => {
        if (interceptor && typeof interceptor.request === 'function') {
          const interceptorReturn = interceptor.request(requestType, apiEndpoint, config);
          if (_.isPromise(interceptorReturn)) {
            interceptorPromises.push(interceptorReturn);
          }
        }
      });
      // Wait for all interceptors to complete
      await Promise.all(interceptorPromises);
    }

    const fullUrlCalled = requestConfigConverter.getFullUrl(apiEndpoint, config);

    Vue.$log.info(`📡 [${requestType.toUpperCase()}] ${encodeURI(fullUrlCalled)}${config ? ' --> params:' : ''}`);
    if (config) {
      const writeValuesToConsole = config.writeValuesToConsole ?? true;
      if (writeValuesToConsole) {
        Vue.$log.info(JSON.stringify(config));
      } else {
        Vue.$log.info(`RequestConfig has been omitted for: ${encodeURI(fullUrlCalled)} `);
      }
    }

    if (requestType === 'get') {
      const cachedPromise = this.cache.get(apiEndpoint, config);
      if (cachedPromise) {
        const response = await cachedPromise;
        Vue.$log.info(`📩 Response [CACHED] ${fullUrlCalled}:`);
        Vue.$log.info(response);
        return response;
      }
    }

    config = config ?? new RequestConfig();
    config.headers = config.headers ?? {};
    config.headers['accept-language'] = Vue.$translation.get();

    const withCredentials = Vue.$config.values['networking-with-credentials'];
    const fetchPromise = Vue.$fetch(requestType, apiEndpoint, {
      withCredentials,
      ...config,
    });

    const handeldPromise = this.handleResponse(fullUrlCalled, config, fetchPromise);
    if (requestType === 'get') {
      this.cache.set(apiEndpoint, config, handeldPromise);
    }

    const response = await handeldPromise;
    Vue.$log.info(`📨 Response ${fullUrlCalled}:`);
    Vue.$log.info(response);
    return response;
  }

  async callInterceptorsResponse(response: ServiceResponse, config: RequestConfig) {
    if (config && config.skipInterceptors) {
      return;
    }
    const interceptorPromises = [] as Array<Promise<any>>;
    this.interceptors.forEach((interceptor) => {
      if (interceptor && typeof interceptor.response === 'function') {
        const interceptorReturn = interceptor.response(response);
        if (_.isPromise(interceptorReturn)) {
          // Typescript doesn't like this even if it is correct, cast to any
          interceptorPromises.push(interceptorReturn as any);
        }
      }
    });
    // Wait for all interceptors to complete
    await Promise.all(interceptorPromises);
  }

  handleResponse(calledUrl: string, config: RequestConfig, promise: Promise<any>): Promise<any> {
    return new Promise((resolve, reject) => {
      promise
        .then((response) => {
          response = response ?? {} as any;
          const data = response.data ?? {};
          const headers = response.headers ?? {};
          const contentType = headers['content-type'] as string ?? '';
          const { status } = response;
          const info = data.info ?? {};
          const { message } = info;
          const { detailMessage } = info;
          const { isError } = info;

          let serviceResponse = new ServiceResponse(response, false, message, detailMessage);

          if (isError) {
            Vue.$log.error(`Response returned error: ${message} - ${detailMessage}`);
          }

          // Specifically check if data was set or not
          if (!_.isSet(response.data)) {
            if (status !== 204) {
              Vue.$log.warning(`No response for: ${calledUrl}, but no 204 status was given`);
            }
            serviceResponse = new ServiceResponse(response, false, Vue.$t('core.error.network.noResponse')!, Vue.$t('core.error.generic.noDetailMessage'));
          } else {
            if (contentType.includes('json')) {
              if (_.isObject(data) && !_.isObject(data.info)) {
                Vue.$log.warning('Response envelope not set! info is not an object - this may be a serialization issue.');
              }
            }
            serviceResponse = new ServiceResponse(response, isError, message, detailMessage);
          }
          this.callInterceptorsResponse(serviceResponse, config);
          resolve(serviceResponse);
        })
        .catch((error: ExtendedFetchFunctionReturn<any> | Error) => {
          const response = (error ?? {}) as ExtendedFetchFunctionReturn<any>;
          Vue.$log.error(`📨 Error querying ${calledUrl}: ${response.status ?? 'Error'} - ${response.statusText ?? response}`);
          let serviceResponse = new ServiceResponse(response, true, Vue.$t('core.error.network.unknown')!, Vue.$t('core.error.generic.noDetailMessage'));

          if ((error as Error)?.message === 'Failed to fetch' && response.data === undefined) {
            // Apparently no connection
            serviceResponse = new ServiceResponse(response, true, Vue.$t('core.error.network.noServerConnection')!);
          } else {
            const { status } = response;
            const data = response.data ?? {};
            const info = data.info ?? data.Info ?? {};
            const errorInfo = info.errorInfo ?? info.ErrorInfo ?? {};
            let message = errorInfo.title ?? errorInfo.Title ?? info.message;
            let { detailMessage } = info;

            message = Vue.$tAlt(message, message);
            detailMessage = Vue.$tAlt(detailMessage, detailMessage);
            serviceResponse = new ServiceResponse(response, true, message, detailMessage);

            let computedMessage;
            if (typeof message === 'string') {
              computedMessage = `${message}`;
              if (typeof detailMessage === 'string') {
                computedMessage = `${message}, \n${detailMessage}`;
              }
            }
            switch (status) {
              case 400:
                serviceResponse = new ServiceResponse(response, true, Vue.$t('core.error.network.badRequest')!, computedMessage);
                break;
              case 401:
                serviceResponse = new ServiceResponse(response, true, Vue.$t('core.error.network.unauthenticated')!, computedMessage);
                break;
              case 403:
                serviceResponse = new ServiceResponse(response, true, Vue.$t('core.error.network.unauthorized')!, computedMessage);
                break;
              case 404:
                serviceResponse = new ServiceResponse(response, true, Vue.$t('core.error.network.notFound')!, computedMessage);
                break;
              case 500:
                serviceResponse = new ServiceResponse(response, true, Vue.$t('core.error.network.server')!, computedMessage);
                break;
              default:
                break;
            }

            Vue.$log.error(serviceResponse);
          }
          this.callInterceptorsResponse(serviceResponse, config);
          reject(serviceResponse);
        });
    });
  }
}
